import { initI18n, ThemeProvider, Toaster } from '@btsis/ui';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { trTR } from '@mui/x-date-pickers/locales';
import 'dayjs/locale/tr';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useRouteError,
} from 'react-router';
import { FINANCE_GUIDE_PERMISSIONS } from '@/features/finance-guide/finance-guide-access';
import { LegacyOperationsRedirect } from '@/features/operations/legacy-redirect';
import { DashboardLayout } from '@/layouts/dashboard-layout';
import { AuthProvider, useAuth } from '@/lib/auth-context';
import { ErrorBoundary } from '@/lib/error-boundary';
import { RequirePermission } from '@/lib/require-permission';
import { LoginPage } from '@/pages/login-page';

initI18n();

// Route-level code splitting: the gallery alone pulls in the DataGrid and the
// date pickers, and no screen should pay for what it does not render.
const DashboardPage = lazy(() =>
  import('@/pages/dashboard-page').then((m) => ({ default: m.DashboardPage })),
);
const GalleryPage = lazy(() =>
  import('@/pages/gallery-page').then((m) => ({ default: m.GalleryPage })),
);
const GeneratedFilesPage = lazy(() =>
  import('@/pages/generated-files-page').then((m) => ({ default: m.GeneratedFilesPage })),
);
const OrganizationPage = lazy(() =>
  import('@/pages/organization-page').then((m) => ({ default: m.OrganizationPage })),
);
const ProfilePage = lazy(() =>
  import('@/pages/profile-page').then((m) => ({ default: m.ProfilePage })),
);
const OrgUnitLevelPage = lazy(() =>
  import('@/pages/org-unit-level-page').then((m) => ({ default: m.OrgUnitLevelPage })),
);
const OrganizationsPage = lazy(() =>
  import('@/pages/organizations-page').then((m) => ({ default: m.OrganizationsPage })),
);
const OrganizationDetailPage = lazy(() =>
  import('@/pages/organization-detail-page').then((m) => ({
    default: m.OrganizationDetailPage,
  })),
);
const UsersPage = lazy(() => import('@/pages/users-page').then((m) => ({ default: m.UsersPage })));
const RolesPage = lazy(() => import('@/pages/roles-page').then((m) => ({ default: m.RolesPage })));
const CampaignsPage = lazy(() =>
  import('@/pages/campaigns-page').then((m) => ({ default: m.CampaignsPage })),
);
const CampaignDetailPage = lazy(() =>
  import('@/pages/campaign-detail-page').then((m) => ({ default: m.CampaignDetailPage })),
);
const CampaignCategoriesPage = lazy(() =>
  import('@/pages/campaign-categories-page').then((m) => ({ default: m.CampaignCategoriesPage })),
);
const CampaignStaffPage = lazy(() =>
  import('@/pages/campaign-staff-page').then((m) => ({ default: m.CampaignStaffPage })),
);
const MyAssignmentsPage = lazy(() =>
  import('@/pages/my-assignments-page').then((m) => ({ default: m.MyAssignmentsPage })),
);
const PrintoutTemplatesPage = lazy(() =>
  import('@/pages/printout-templates-page').then((m) => ({ default: m.PrintoutTemplatesPage })),
);
const PrintoutTemplateEditorPage = lazy(() =>
  import('@/pages/printout-template-editor-page').then((m) => ({
    default: m.PrintoutTemplateEditorPage,
  })),
);
const DonorsPage = lazy(() =>
  import('@/pages/donors-page').then((m) => ({ default: m.DonorsPage })),
);
const DonorDuplicatesPage = lazy(() =>
  import('@/pages/donor-duplicates-page').then((m) => ({ default: m.DonorDuplicatesPage })),
);
const DonationsPage = lazy(() =>
  import('@/pages/donations-page').then((m) => ({ default: m.DonationsPage })),
);
const BulkChangePage = lazy(() =>
  import('@/pages/bulk-change-page').then((m) => ({ default: m.BulkChangePage })),
);
const BulkChangeDetailPage = lazy(() =>
  import('@/pages/bulk-change-detail-page').then((m) => ({ default: m.BulkChangeDetailPage })),
);
const DonationImportsPage = lazy(() =>
  import('@/pages/donation-imports-page').then((m) => ({ default: m.DonationImportsPage })),
);
const DonationImportNewPage = lazy(() =>
  import('@/pages/donation-import-new-page').then((m) => ({ default: m.DonationImportNewPage })),
);
const DonationImportDetailPage = lazy(() =>
  import('@/pages/donation-import-detail-page').then((m) => ({
    default: m.DonationImportDetailPage,
  })),
);
const DeletedDonationsPage = lazy(() =>
  import('@/pages/deleted-donations-page').then((m) => ({ default: m.DeletedDonationsPage })),
);
const DeletedDonationDetailPage = lazy(() =>
  import('@/pages/deleted-donation-detail-page').then((m) => ({
    default: m.DeletedDonationDetailPage,
  })),
);
const DonationEntryPage = lazy(() =>
  import('@/pages/donation-entry-page').then((m) => ({ default: m.DonationEntryPage })),
);
const DonationDetailPage = lazy(() =>
  import('@/pages/donation-detail-page').then((m) => ({ default: m.DonationDetailPage })),
);
const DonationEditPage = lazy(() =>
  import('@/pages/donation-edit-page').then((m) => ({ default: m.DonationEditPage })),
);
const OperationCampaignsPage = lazy(() =>
  import('@/pages/operation-campaigns-page').then((m) => ({
    default: m.OperationCampaignsPage,
  })),
);
const OperationCampaignPage = lazy(() =>
  import('@/pages/operation-campaign-page').then((m) => ({ default: m.OperationCampaignPage })),
);
const PrintoutListsPage = lazy(() =>
  import('@/pages/printout-lists-page').then((m) => ({ default: m.PrintoutListsPage })),
);
const PrintoutListDetailPage = lazy(() =>
  import('@/pages/printout-list-detail-page').then((m) => ({
    default: m.PrintoutListDetailPage,
  })),
);
const DonationBoxesPage = lazy(() =>
  import('@/pages/donation-boxes-page').then((m) => ({ default: m.DonationBoxesPage })),
);
const DonationBoxDetailPage = lazy(() =>
  import('@/pages/donation-box-detail-page').then((m) => ({ default: m.DonationBoxDetailPage })),
);
const UserCommitmentsPage = lazy(() =>
  import('@/pages/user-commitments-page').then((m) => ({ default: m.UserCommitmentsPage })),
);
const PledgesPage = lazy(() =>
  import('@/pages/pledges-page').then((m) => ({ default: m.PledgesPage })),
);
const ExchangeRatesPage = lazy(() =>
  import('@/pages/exchange-rates-page').then((m) => ({ default: m.ExchangeRatesPage })),
);
const FinanceGuidePage = lazy(() =>
  import('@/pages/finance-guide-page').then((m) => ({ default: m.FinanceGuidePage })),
);
const StatementImportsPage = lazy(() =>
  import('@/pages/statement-imports-page').then((m) => ({ default: m.StatementImportsPage })),
);
const StatementProfilesPage = lazy(() =>
  import('@/pages/statement-profiles-page').then((m) => ({ default: m.StatementProfilesPage })),
);
const StatementProfileEditorPage = lazy(() =>
  import('@/pages/statement-profile-editor-page').then((m) => ({
    default: m.StatementProfileEditorPage,
  })),
);
const StatementImportDetailPage = lazy(() =>
  import('@/pages/statement-import-detail-page').then((m) => ({
    default: m.StatementImportDetailPage,
  })),
);
const ReconciliationPage = lazy(() =>
  import('@/pages/reconciliation-page').then((m) => ({ default: m.ReconciliationPage })),
);
const ReconciliationBasketPage = lazy(() =>
  import('@/pages/reconciliation-basket-page').then((m) => ({
    default: m.ReconciliationBasketPage,
  })),
);
const ReconciliationShortfallsPage = lazy(() =>
  import('@/pages/reconciliation-shortfalls-page').then((m) => ({
    default: m.ReconciliationShortfallsPage,
  })),
);
const PayoutReconciliationPage = lazy(() =>
  import('@/pages/payout-reconciliation-page').then((m) => ({
    default: m.PayoutReconciliationPage,
  })),
);
const InboundMediaPage = lazy(() =>
  import('@/pages/inbound-media-page').then((m) => ({ default: m.InboundMediaPage })),
);
const RefundsPage = lazy(() =>
  import('@/pages/refunds-page').then((m) => ({ default: m.RefundsPage })),
);
const FundTransfersPage = lazy(() =>
  import('@/pages/fund-transfers-page').then((m) => ({ default: m.FundTransfersPage })),
);
const InstitutionBalancesPage = lazy(() =>
  import('@/pages/institution-balances-page').then((m) => ({ default: m.InstitutionBalancesPage })),
);
const InstitutionVaultsPage = lazy(() =>
  import('@/pages/institution-vaults-page').then((m) => ({ default: m.InstitutionVaultsPage })),
);
const InstitutionStatementPage = lazy(() =>
  import('@/pages/institution-statement-page').then((m) => ({
    default: m.InstitutionStatementPage,
  })),
);
const FinanceBalancesPage = lazy(() =>
  import('@/pages/finance-balances-page').then((m) => ({ default: m.FinanceBalancesPage })),
);
const FinanceConsolidationPage = lazy(() =>
  import('@/pages/finance-consolidation-page').then((m) => ({
    default: m.FinanceConsolidationPage,
  })),
);
const ReceiptsPage = lazy(() =>
  import('@/pages/receipts-page').then((m) => ({ default: m.ReceiptsPage })),
);
const AnnualCertificatesPage = lazy(() =>
  import('@/pages/annual-certificates-page').then((m) => ({ default: m.AnnualCertificatesPage })),
);
const AccountTransactionsPage = lazy(() =>
  import('@/pages/account-transactions-page').then((m) => ({
    default: m.AccountTransactionsPage,
  })),
);
const AccountTransactionDetailPage = lazy(() =>
  import('@/pages/account-transaction-detail-page').then((m) => ({
    default: m.AccountTransactionDetailPage,
  })),
);
const DonorDetailPage = lazy(() =>
  import('@/pages/donor-detail-page').then((m) => ({ default: m.DonorDetailPage })),
);
const DonorLinkRequestsPage = lazy(() =>
  import('@/pages/donor-link-requests-page').then((m) => ({ default: m.DonorLinkRequestsPage })),
);
const AuditLogPage = lazy(() =>
  import('@/pages/audit-log-page').then((m) => ({ default: m.AuditLogPage })),
);

// PRD §10's reports. Lazy like every other screen, and not only for the
// splitting: the reports are the one place ApexCharts is reached for, and
// `packages/ui`'s `Chart` keeps it out of the chunk until a chart renders.
const ReportsPage = lazy(() =>
  import('@/pages/reports-page').then((m) => ({ default: m.ReportsPage })),
);
const JobFailuresPage = lazy(() =>
  import('@/pages/job-failures-page').then((m) => ({ default: m.JobFailuresPage })),
);
const IntegrationsPage = lazy(() =>
  import('@/pages/integrations-page').then((m) => ({ default: m.IntegrationsPage })),
);
const ScheduledRunsPage = lazy(() =>
  import('@/pages/scheduled-runs-page').then((m) => ({ default: m.ScheduledRunsPage })),
);
const NotificationsPage = lazy(() =>
  import('@/pages/notifications-page').then((m) => ({ default: m.NotificationsPage })),
);
const TwoFactorSetupPage = lazy(() =>
  import('@/pages/two-factor-setup-page').then((m) => ({ default: m.TwoFactorSetupPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/pages/forgot-password-page').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ConfirmEmailPage = lazy(() =>
  import('@/pages/confirm-email-page').then((m) => ({ default: m.ConfirmEmailPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/reset-password-page').then((m) => ({ default: m.ResetPasswordPage })),
);

const LEVEL_PATHS = { region: 'regions', zone: 'zones', country: 'countries' } as const;

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
});

function FullScreenLoader() {
  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
      <CircularProgress />
    </Box>
  );
}

function RequireAuth() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  // A role that demands a second factor blocks everything else until it is set
  // up. The API enforces the same thing; this only avoids a screen full of 403s.
  if (user.mustEnrolTwoFactor && !user.impersonation)
    return <Navigate to="/two-factor-setup" replace />;
  return <Outlet />;
}

function RequireTwoFactorSetup() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.impersonation || !user.mustEnrolTwoFactor) return <Navigate to="/" replace />;
  return (
    <Suspense fallback={<FullScreenLoader />}>
      <TwoFactorSetupPage />
    </Suspense>
  );
}

function RedirectIfAuthenticated() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <FullScreenLoader />;
  if (user) return <Navigate to="/" replace />;
  return <Outlet />;
}

/**
 * A data router, so a screen holding unsaved changes can block navigation with
 * `useBlocker`. A render error is rethrown past the router's own boundary to
 * the app's `ErrorBoundary`, as it reached it before.
 */
function RethrowRouteError(): never {
  throw useRouteError();
}

function RootRoute() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootRoute />} errorElement={<RethrowRouteError />}>
      <Route element={<RedirectIfAuthenticated />}>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/forgot-password"
          element={
            <Suspense fallback={<FullScreenLoader />}>
              <ForgotPasswordPage />
            </Suspense>
          }
        />
      </Route>
      {/* Reachable while signed in too: a reset link may be opened
        in a browser that still holds an old session. */}
      <Route
        path="/reset-password"
        element={
          <Suspense fallback={<FullScreenLoader />}>
            <ResetPasswordPage />
          </Suspense>
        }
      />
      {/* Reachable signed out too, where it asks the reader to sign in first:
        the link arrives in a mailbox that may be opened anywhere. */}
      <Route
        path="/confirm-email"
        element={
          <Suspense fallback={<FullScreenLoader />}>
            <ConfirmEmailPage />
          </Suspense>
        }
      />
      <Route path="/two-factor-setup" element={<RequireTwoFactorSetup />} />
      <Route element={<RequireAuth />}>
        <Route
          element={
            <Suspense fallback={<FullScreenLoader />}>
              <DashboardLayout />
            </Suspense>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="gallery" element={<GalleryPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="my-assignments" element={<MyAssignmentsPage />} />
          <Route
            path="audit-log"
            element={
              <RequirePermission permission="auditLog.list">
                <AuditLogPage />
              </RequirePermission>
            }
          />
          <Route
            path="reports"
            element={
              <RequirePermission permission="report.view">
                <ReportsPage />
              </RequirePermission>
            }
          />
          <Route path="files" element={<GeneratedFilesPage />} />
          <Route
            path="jobs/failures"
            element={
              <RequirePermission permission="jobs.view">
                <JobFailuresPage />
              </RequirePermission>
            }
          />
          <Route
            path="integrations"
            element={
              <RequirePermission permission="integration.view">
                <IntegrationsPage />
              </RequirePermission>
            }
          />
          <Route
            path="jobs/runs"
            element={
              <RequirePermission permission="jobs.view">
                <ScheduledRunsPage />
              </RequirePermission>
            }
          />
          <Route
            path="notifications"
            element={
              <RequirePermission permission="notification.view">
                <NotificationsPage />
              </RequirePermission>
            }
          />
          <Route
            path="users"
            element={
              <RequirePermission permission="user.list">
                <UsersPage />
              </RequirePermission>
            }
          />
          <Route
            path="roles"
            element={
              <RequirePermission permission="role.list">
                <RolesPage />
              </RequirePermission>
            }
          />
          <Route
            path="campaigns"
            element={
              <RequirePermission permission="campaign.list">
                <CampaignsPage />
              </RequirePermission>
            }
          />
          {/* The tab is a route segment so a tab is linkable; the
            bare id redirects to the first one. */}
          <Route path="campaigns/:id" element={<Navigate to="general" replace />} />
          <Route
            path="campaigns/:id/:tab"
            element={
              <RequirePermission permission="campaign.view">
                <CampaignDetailPage />
              </RequirePermission>
            }
          />
          {/* Declared before the id route so the inbox path is not read as a donor id. */}
          <Route
            path="donations/new"
            element={
              <RequirePermission permission="donation.create">
                <DonationEntryPage />
              </RequirePermission>
            }
          />

          {/* Declared before the id route so `queue` is not read as a donation id.
              The queue screen is the operations page's order since
              docs/decisions/0091, and its old address lands there. */}
          <Route path="donations/queue" element={<LegacyOperationsRedirect />} />

          {/* *Operasyon İşlemleri* — docs/decisions/0091. Behind
              `donation.fulfill`, which a staff assignment derives. */}
          <Route
            path="operations/campaigns"
            element={
              <RequirePermission permission="donation.fulfill" derivable>
                <OperationCampaignsPage />
              </RequirePermission>
            }
          />
          <Route
            path="operations/campaigns/:campaignId"
            element={
              <RequirePermission permission="donation.fulfill" derivable>
                <OperationCampaignPage />
              </RequirePermission>
            }
          />
          {/* *PDF Yönetimi* — docs/decisions/0092. Behind `donation.print`,
              which a staff assignment derives. */}
          <Route
            path="operations/printout-lists"
            element={
              <RequirePermission permission="donation.print" derivable>
                <PrintoutListsPage />
              </RequirePermission>
            }
          />
          <Route
            path="operations/printout-lists/:listId"
            element={
              <RequirePermission permission="donation.print" derivable>
                <PrintoutListDetailPage />
              </RequirePermission>
            }
          />
          {/* *Gelen Medya* — docs/decisions/0093. A role's `inboundMedia.list`,
              or a staff assignment, whose `donation.fulfill` reads the matched
              media of its work. */}
          <Route
            path="operations/inbound-media"
            element={
              <RequirePermission permission="inboundMedia.list" staffPermission="donation.fulfill">
                <InboundMediaPage />
              </RequirePermission>
            }
          />

          {/* Declared before the id route so `deleted` is not read as a donation id. */}
          <Route
            path="donations/deleted"
            element={
              <RequirePermission permission="donation.viewDeleted">
                <DeletedDonationsPage />
              </RequirePermission>
            }
          />

          {/* A deleted donation's read-only page, behind the trash's own
              permission rather than `donation.view` — docs/decisions/0085. */}
          <Route
            path="donations/deleted/:id"
            element={
              <RequirePermission permission="donation.viewDeleted">
                <DeletedDonationDetailPage />
              </RequirePermission>
            }
          />

          {/* Bulk add and update by template — docs/decisions/0125. Each kind's own
              list permission opens its page; the page and the server ask the full
              pair, and the entry is a list action, not a sidebar leaf. */}
          <Route
            path="bulk-changes/:kind"
            element={
              <RequirePermission anyOf={['user.list', 'orgUnit.list', 'donation.list']}>
                <BulkChangePage />
              </RequirePermission>
            }
          />
          <Route
            path="bulk-changes/:kind/:id"
            element={
              <RequirePermission anyOf={['user.list', 'orgUnit.list', 'donation.list']}>
                <BulkChangeDetailPage />
              </RequirePermission>
            }
          />

          {/* A season's own Excel list, entered as staff entry — docs/decisions/0104.
              Declared before `donations/:id` so `imports` is never read as a
              donation id. */}
          <Route
            path="donations/imports"
            element={
              <RequirePermission permission="donation.import">
                <DonationImportsPage />
              </RequirePermission>
            }
          />
          <Route
            path="donations/imports/new"
            element={
              <RequirePermission permission="donation.import">
                <DonationImportNewPage />
              </RequirePermission>
            }
          />
          <Route
            path="donations/imports/:id"
            element={
              <RequirePermission permission="donation.import">
                <DonationImportDetailPage />
              </RequirePermission>
            }
          />

          <Route
            path="donations"
            element={
              <RequirePermission permission="donation.list" derivable>
                <LegacyOperationsRedirect>
                  <DonationsPage />
                </LegacyOperationsRedirect>
              </RequirePermission>
            }
          />

          <Route
            path="donations/:id"
            element={
              <RequirePermission permission="donation.view" derivable>
                <DonationDetailPage />
              </RequirePermission>
            }
          />

          <Route
            path="donations/:id/edit"
            element={
              <RequirePermission permission="donation.edit">
                <DonationEditPage />
              </RequirePermission>
            }
          />

          <Route
            path="donation-boxes"
            element={
              <RequirePermission permission="donationBox.list">
                <DonationBoxesPage />
              </RequirePermission>
            }
          />
          <Route
            path="donation-boxes/:id"
            element={
              <RequirePermission permission="donationBox.view">
                <DonationBoxDetailPage />
              </RequirePermission>
            }
          />

          <Route
            path="accounting/user-commitments"
            element={
              <RequirePermission permission="report.view">
                <UserCommitmentsPage />
              </RequirePermission>
            }
          />

          <Route
            path="pledges"
            element={
              <RequirePermission permission="pledge.list">
                <PledgesPage />
              </RequirePermission>
            }
          />

          <Route
            path="exchange-rates"
            element={
              <RequirePermission permission="exchangeRate.list">
                <ExchangeRatesPage />
              </RequirePermission>
            }
          />

          {/* *Muhasebe Kılavuzu* — for whoever reads any finance list. */}
          <Route
            path="help/finance"
            element={
              <RequirePermission anyOf={FINANCE_GUIDE_PERMISSIONS}>
                <FinanceGuidePage />
              </RequirePermission>
            }
          />
          <Route
            path="statement-imports"
            element={
              <RequirePermission permission="statement.list" organizationScoped>
                <StatementImportsPage />
              </RequirePermission>
            }
          />
          <Route
            path="statement-profiles"
            element={
              <RequirePermission permission="statement.list" organizationScoped>
                <StatementProfilesPage />
              </RequirePermission>
            }
          />
          <Route
            path="statement-profiles/new"
            element={
              <RequirePermission permission="statement.import" organizationScoped>
                <StatementProfileEditorPage />
              </RequirePermission>
            }
          />
          <Route
            path="statement-imports/:id"
            element={
              <RequirePermission permission="statement.view" organizationScoped>
                <StatementImportDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="reconciliation"
            element={
              <RequirePermission permission="reconciliation.list" organizationScoped>
                <ReconciliationPage />
              </RequirePermission>
            }
          />
          <Route
            path="reconciliation/basket"
            element={
              <RequirePermission permission="reconciliation.match" organizationScoped>
                <ReconciliationBasketPage />
              </RequirePermission>
            }
          />
          <Route
            path="reconciliation/shortfalls"
            element={
              <RequirePermission permission="reconciliation.list" organizationScoped>
                <ReconciliationShortfallsPage />
              </RequirePermission>
            }
          />
          <Route
            path="reconciliation/payouts"
            element={
              <RequirePermission permission="reconciliation.list" organizationScoped>
                <PayoutReconciliationPage />
              </RequirePermission>
            }
          />
          {/* Gelen Medya's address before it joined Operasyon İşlemleri. */}
          <Route path="inbound-media" element={<LegacyOperationsRedirect />} />
          <Route
            path="refunds"
            element={
              <RequirePermission permission="refund.list" organizationScoped>
                <RefundsPage />
              </RequirePermission>
            }
          />
          <Route
            path="fund-transfers"
            element={
              <RequirePermission permission="fundTransfer.list" organizationScoped>
                <FundTransfersPage />
              </RequirePermission>
            }
          />
          <Route
            path="finance/institutions"
            element={
              <RequirePermission permission="finance.view">
                <InstitutionBalancesPage />
              </RequirePermission>
            }
          />
          <Route
            path="finance/institutions/:id"
            element={
              <RequirePermission permission="finance.view">
                <InstitutionStatementPage />
              </RequirePermission>
            }
          />
          <Route
            path="finance/institutions/:id/vaults"
            element={
              <RequirePermission permission="finance.view">
                <InstitutionVaultsPage />
              </RequirePermission>
            }
          />
          <Route
            path="finance/balances"
            element={
              <RequirePermission permission="finance.view" organizationScoped>
                <FinanceBalancesPage />
              </RequirePermission>
            }
          />
          <Route
            path="finance/consolidation"
            element={
              <RequirePermission permission="finance.view" organizationScoped>
                <FinanceConsolidationPage />
              </RequirePermission>
            }
          />
          <Route
            path="account-transactions"
            element={
              <RequirePermission permission="statement.list" organizationScoped>
                <AccountTransactionsPage />
              </RequirePermission>
            }
          />
          <Route
            path="account-transactions/:id"
            element={
              <RequirePermission permission="statement.view" organizationScoped>
                <AccountTransactionDetailPage />
              </RequirePermission>
            }
          />

          <Route
            path="receipts"
            element={
              <RequirePermission permission="receipt.list">
                <ReceiptsPage />
              </RequirePermission>
            }
          />
          <Route
            path="receipts/annual-certificates"
            element={
              <RequirePermission permission="receipt.list">
                <AnnualCertificatesPage />
              </RequirePermission>
            }
          />

          <Route
            path="donors/link-requests"
            element={
              <RequirePermission permission="donor.edit">
                <DonorLinkRequestsPage />
              </RequirePermission>
            }
          />
          <Route
            path="donors/duplicates"
            element={
              <RequirePermission permission="donor.merge">
                <DonorDuplicatesPage />
              </RequirePermission>
            }
          />
          <Route
            path="donors"
            element={
              <RequirePermission permission="donor.list">
                <DonorsPage />
              </RequirePermission>
            }
          />
          <Route
            path="donors/:id"
            element={
              <RequirePermission permission="donor.view">
                <DonorDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="campaign-categories"
            element={
              <RequirePermission permission="campaignCategory.list">
                <CampaignCategoriesPage />
              </RequirePermission>
            }
          />
          <Route
            path="campaign-staff"
            element={
              <RequirePermission permission="campaignStaff.view">
                <CampaignStaffPage />
              </RequirePermission>
            }
          />
          <Route
            path="printout-templates"
            element={
              <RequirePermission permission="campaign.view">
                <PrintoutTemplatesPage />
              </RequirePermission>
            }
          />
          <Route
            path="printout-templates/:id"
            element={
              <RequirePermission permission="campaign.view">
                <PrintoutTemplateEditorPage />
              </RequirePermission>
            }
          />
          {(['region', 'zone', 'country'] as const).map((level) => (
            <Route
              key={level}
              path={LEVEL_PATHS[level]}
              element={
                <RequirePermission permission="orgUnit.list">
                  <OrgUnitLevelPage key={level} level={level} />
                </RequirePermission>
              }
            />
          ))}
          <Route
            path="institutions"
            element={
              <RequirePermission permission="orgUnit.list">
                <OrganizationPage />
              </RequirePermission>
            }
          />
          <Route
            path="organizations"
            element={
              <RequirePermission permission="organization.list" organizationScoped>
                <OrganizationsPage />
              </RequirePermission>
            }
          />
          <Route
            path="organizations/:id"
            element={
              <RequirePermission permission="organization.view" organizationScoped>
                <OrganizationDetailPage />
              </RequirePermission>
            }
          />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>,
  ),
);

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="tr"
            localeText={trTR.components.MuiLocalizationProvider.defaultProps.localeText}
          >
            <Toaster />
            <RouterProvider router={router} />
          </LocalizationProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
